import type paper from "paper";
import { pieceStitchLines, type Project, type Piece } from "../model/Project";
import {
  effectiveStitchSpacing,
  effectiveSkipEvery,
} from "./../model/stitching";
import { placedStitchHoles } from "../model/placedStitchHoles";
import { stitchJoints, type JointEnd } from "../model/stitchJoints";
import { usesCornerLayout } from "../model/stitchLayout";
import { isFeatureEnabled } from "../featureFlags";
import type { StampLibrary } from "../stamps/StampLibrary";

/** "2" -> "2nd", "3" -> "3rd", etc. for the skip-every-Nth note. */
function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

/** Tutorial for the "skipping the stitch" saddle-stitch technique (#687),
 *  linked from the skip-stitch instruction note. Kept as one constant so the
 *  reference can be swapped without touching the wording. */
export const SKIP_STITCH_TECHNIQUE_URL =
  "https://www.youtube.com/watch?v=Gv-hlZ4q1Vo&t=1202s";

/** A clickable link a renderer can place over `phrase` within a Step's
 *  `description` (the PDF instructions place a hyperlink over the matching
 *  words). */
export interface StepLink {
  phrase: string;
  url: string;
}

export type StepPhase =
  | "design"
  | "cut-outline"
  | "punch-hole"
  | "casing"
  | "stamping"
  | "lining"
  | "finishing";

export interface Step {
  phase: StepPhase;
  title: string;
  /** May contain `\n\n`-separated paragraphs. */
  description: string;
  tools: string[];
  /** Hyperlinks placed over phrases in `description` (PDF only). */
  links?: StepLink[];
}

const PHASE_ORDER: StepPhase[] = [
  "design",
  "cut-outline",
  "punch-hole",
  "casing",
  "stamping",
  "lining",
  "finishing",
];

export interface StepOptions {
  /** Whether the pattern being produced actually carries the point markers
   *  (#693) — the print dialog's Content toggle. Off, the "line up the parts"
   *  step is dropped: it talks about marks the sheet doesn't have, and steps
   *  that describe a pattern you aren't holding are worse than none. */
  pointMarkers?: boolean;
}

export function extractSteps(
  project: Project,
  library: StampLibrary,
  scope: paper.PaperScope,
  options: StepOptions = {},
): Step[] {
  const steps: Step[] = [];

  if (project.pieces.length === 0) {
    return [
      {
        phase: "design",
        title: "Design empty",
        description:
          "Start by sketching the outline of your leather part, then add holes, cuts, and stamps.",
        tools: [],
      },
    ];
  }

  steps.push({
    phase: "design",
    title: "Transfer pattern to leather",
    description:
      "Lay the printed pattern on the leather and trace the outline and decoration guides. On cased (damp) vegetable-tanned leather a stylus leaves a clean impression; on other leathers use a silver marking pen or another removable marker that won't damage the surface.",
    tools: ["Stylus", "Silver marking pen", "Pattern paper", "Tape"],
  });

  for (const piece of project.pieces)
    addPieceSteps(piece, steps, library, scope);

  if (options.pointMarkers !== false)
    addJointAlignmentStep(project, steps, scope);

  steps.push({
    phase: "finishing",
    title: "Finish edges and condition leather",
    description:
      "Bevel and burnish the cut edges, then apply leather conditioner or finish of your choice. Let dry before assembly.",
    tools: ["Edge beveller", "Burnishing tool", "Leather conditioner"],
  });

  return steps.sort(
    (a, b) => PHASE_ORDER.indexOf(a.phase) - PHASE_ORDER.indexOf(b.phase),
  );
}

/**
 * Say in words which hole meets which (#693).
 *
 * The pattern rings the shared holes and letters them, but a letter on its own
 * doesn't say which parts it joins or how far along each row it sits — and once
 * the parts are cut out, the overlap that made it obvious is gone. So each joint
 * gets a sentence naming every one of its ends by ordinal, part and seam.
 */
function addJointAlignmentStep(
  project: Project,
  steps: Step[],
  scope: paper.PaperScope,
) {
  if (!isFeatureEnabled("stitch-matching")) return;
  const joints = stitchJoints(project, scope);
  if (joints.length === 0) return;
  // The letters are behind `point-letters`. Without them the step still says
  // exactly which holes to align — by ordinal, seam and part — since that is
  // the information; the letter is a shorthand for finding it on the leather.
  const lettered = isFeatureEnabled("point-letters");
  const where = (end: JointEnd) =>
    end.index === 0
      ? `the starting hole of "${end.stitchName}" on "${end.pieceName}"`
      : `the ${ordinal(end.index + 1)} hole of "${end.stitchName}" on "${end.pieceName}"`;
  const lines = joints.map((j) => {
    const ends = j.ends.map(where).join(", and ");
    return lettered
      ? `Align the holes marked ${j.label}: ${ends}.`
      : `Align ${ends}.`;
  });
  steps.push({
    phase: "finishing",
    title: `Line up the parts to sew (${joints.length} joint${joints.length === 1 ? "" : "s"})`,
    description: [
      lettered
        ? `These seams were laid out on a shared grid, so their holes meet one another exactly — but only from the right hole. Each joint is marked on the pattern and lettered; put the holes wearing the same letter together and the rows run in step from there, every hole after it landing on its partner.`
        : `These seams were laid out on a shared grid, so their holes meet one another exactly — but only from the right hole. Each joint is marked on the pattern with a chevron pointing at the hole; put the marked holes together and the rows run in step from there, every hole after it landing on its partner.`,
      ...lines,
    ].join("\n\n"),
    tools: ["Stitching pony", "Awl"],
  });
}

function addPieceSteps(
  piece: Piece,
  steps: Step[],
  library: StampLibrary,
  scope: paper.PaperScope,
) {
  steps.push({
    phase: "cut-outline",
    title: `Cut outline of "${piece.name}"`,
    description: `Cut along the outline${piece.cutouts.length ? " and any internal cutouts" : ""} using a sharp knife or rotary cutter.`,
    tools: ["Round knife or rotary cutter", "Cutting mat", "Steel ruler"],
  });

  if (piece.holes.length) {
    steps.push({
      phase: "punch-hole",
      title: `Punch ${piece.holes.length} hole${piece.holes.length === 1 ? "" : "s"} on "${piece.name}"`,
      description: `Punch ${piece.holes.length} hole${piece.holes.length === 1 ? "" : "s"} at the marked positions. Use the radius indicated for each hole (default ${piece.holes[0]?.radius ?? 1.5} mm).`,
      tools: ["Hole punch", "Mallet", "Poly cutting board"],
    });
  }

  for (const line of pieceStitchLines(piece)) {
    if (line.visible === false) continue;
    // The human-facing "mark N holes" count reflects only the holes that
    // actually land ON the leather (#910 PR3): route through the shared
    // `placedStitchHoles` retention (work-area window AND on-piece outline) so
    // a seam overflowing its piece instructs the maker to punch FEWER holes,
    // matching what the rendered pattern / export shows. The `conforming`
    // flags used for the off-pitch note below are carried through unchanged.
    const placements = placedStitchHoles(line, piece, scope);
    const count = placements.length;
    if (!count) continue;
    const shapeLabel =
      line.shape === "round"
        ? "scratch awl"
        : line.shape === "diamond"
          ? "diamond chisel"
          : "French/slanted chisel";
    // Use the ACTUAL spacing the holes are marked at — for a matched follower
    // that's its own count-based spacing, not its (or the main's) pitch field.
    const actualPitch = effectiveStitchSpacing(line);
    let lead = `Use a ${shapeLabel} at ${actualPitch.toFixed(2)} mm pitch to mark ${count} stitching hole${count === 1 ? "" : "s"} along "${line.name ?? "the seam"}".`;
    const paragraphs: string[] = [];
    const links: StepLink[] = [];
    // Matched-seam notes (#687): off-pitch holes (same paragraph as the lead),
    // and the skip technique as its OWN paragraph.
    if (isFeatureEnabled("stitch-matching")) {
      const skipEvery = effectiveSkipEvery(line);
      const offPitch = placements.filter((p) => p.conforming === false).length;
      const mainPitch = line.matchSpacing;
      if (offPitch > 0 && mainPitch != null) {
        // Distinguish the two off-pitch situations by MODE, not by how close the
        // pitches happen to be. "Match pitch exactly" lays holes AT the matched
        // pitch and some run off a too-short seam (a genuine overflow);
        // `effectiveStitchSpacing` returns `matchSpacing` verbatim there, so
        // identity means "laid at the matched pitch". Otherwise the row sits at
        // its OWN spread (fill / loop, or a skip that only NARROWS the gap toward
        // the match without closing it) — that used to be mislabelled "can't fit"
        // whenever its own spread landed within 0.01 of the match (#711).
        const laidAtMatched = actualPitch === mainPitch;
        if (laidAtMatched) {
          lead += ` ${offPitch} hole${offPitch === 1 ? "" : "s"} can't fit at the matched ${mainPitch.toFixed(2)} mm pitch (the seam is too short) — the wavy line marks the over-tight stitch${offPitch === 1 ? "" : "es"}; punch ${offPitch === 1 ? "it" : "them"} at the marked position${offPitch === 1 ? "" : "s"} so both seams keep the same hole count.`;
        } else if (actualPitch.toFixed(2) !== mainPitch.toFixed(2)) {
          // Own spread still differs from the match (to the shown precision) —
          // e.g. skipping narrowed the gap but didn't close it. Warn accurately.
          lead += ` These ${count} holes sit at this seam's own pitch (${actualPitch.toFixed(2)} mm), NOT the matched seam's ${mainPitch.toFixed(2)} mm — the wavy line marks where the two pitches differ; punch them as marked so both seams keep the correct hole count.`;
        }
        // else: the row's own spread rounds to the matched pitch → aligned, no
        // note (a skip that landed exactly on the match, or rounds to it).
      }
      paragraphs.push(lead);
      if (skipEvery != null) {
        // "Skipping the stitch" is a saddle-stitch technique for joining seams of
        // UNEQUAL hole counts — you don't leave a hole empty, you sew every hole
        // on one side and double back into the previous hole on the other. So
        // don't describe it as skipping holes; just name the technique.
        paragraphs.push(
          `This seam is punched with fewer holes than its match. When sewing, use the "skipping the stitch" technique so the two rows still meet hole-for-hole.`,
        );
        links.push({
          phrase: "skipping the stitch",
          url: SKIP_STITCH_TECHNIQUE_URL,
        });
      }
      // Main side of the same match (#687): every Nth hole is slashed on the
      // pattern to mark where the shorter match doubles back.
      if (line.skipMarkEvery != null && line.skipMarkEvery >= 2) {
        paragraphs.push(
          `Every ${ordinal(line.skipMarkEvery)} hole on this seam is slashed on the pattern to mark where to "skip" a stitch when sewing — punch all the holes and use the "skipping the stitch" technique so this seam meets its shorter match hole-for-hole.`,
        );
        links.push({
          phrase: "skipping the stitch",
          url: SKIP_STITCH_TECHNIQUE_URL,
        });
      }
    } else {
      paragraphs.push(lead);
    }
    // How to punch a corner-laid row (#691). This is the panel's job done
    // properly: the toggle there is one short label, and the technique it
    // implies belongs with the maker at the bench, not in the settings of
    // someone who has already decided.
    if (usesCornerLayout(line)) {
      const pivots = placements.filter((p) => p.corner === true).length;
      const rounded =
        line.roundCornerHoles === true && line.shape !== "round" && pivots > 0;
      paragraphs.push(
        `Mark the pivot holes on the corners FIRST, then punch each run between them — and each run from an end pivot hole to the end of the seam — as one march at the chisel's own ${actualPitch.toFixed(2)} mm. A run is never a whole number of pitches, and that slack is taken up by the two gaps touching the pivot holes, split equally between them: on the pattern they read slightly wide or slightly tight, evenly on both sides of each turn. Every gap between them is the chisel's exactly. Punching straight through from one end instead pushes the whole leftover to the far end, which is to say onto a corner.` +
          (rounded
            ? ` The ${pivots} pivot hole${pivots === 1 ? "" : "s"} ${pivots === 1 ? "is" : "are"} drawn round: punch ${pivots === 1 ? "it" : "them"} with an awl or a round punch rather than the chisel. A slanted slot at a turn points across the thread's new direction, and the corner is where a seam is most likely to tear.`
            : ""),
      );
    }
    steps.push({
      phase: "punch-hole",
      title: `Mark ${count} stitch hole${count === 1 ? "" : "s"} on "${piece.name}"`,
      description: paragraphs.join("\n\n"),
      tools: ["Pricking iron / chisel", "Mallet", "Poly cutting board"],
      ...(links.length > 0 ? { links } : {}),
    });
  }

  // Decorations contributing to stamping phase
  const decorTools = new Set<string>();
  let stampCount = 0;
  let liningCount = 0;
  for (const dec of piece.decorations) {
    if (!dec.visible) continue;
    if (
      dec.kind === "stamp" ||
      dec.kind === "lineStamp" ||
      dec.kind === "fill"
    ) {
      const meta = library.get(dec.stampId);
      if (meta) decorTools.add(meta.name);
      stampCount += 1;
    } else if (dec.kind === "freePath" || dec.kind === "offset") {
      liningCount += 1;
    }
  }

  if (stampCount > 0) {
    steps.push({
      phase: "casing",
      title: `Case "${piece.name}"`,
      description:
        "Lightly dampen the leather with a sponge until uniformly cool to the touch. Wait until surface returns to natural color before stamping.",
      tools: ["Sponge", "Water"],
    });
    steps.push({
      phase: "stamping",
      title: `Stamp "${piece.name}" (${stampCount} placements)`,
      description: `Apply each stamp at the indicated position, rotation and depth. Strike with consistent force using a poly mallet. Work outwards from the center to avoid distorting set impressions.`,
      tools: ["Poly mallet", ...decorTools],
    });
  }

  if (liningCount > 0) {
    steps.push({
      phase: "lining",
      title: `Cut decorative lines on "${piece.name}"`,
      description:
        "Use a swivel knife or stylus to follow the line guides, then deepen with a beveller along the outside of the cut.",
      tools: ["Swivel knife", "Edge beveller"],
    });
  }
}
